import { defineStore } from "pinia";
import { handleApiResponse, handleError } from "@/utils/common.js";
import {
  CUSTOMER_OBJECT,
  UPDATE_CUSTOMER_OBJECT_FIELD,
  THUMBNAIL_UPLOAD,
  GET_THUMBNAIL,
} from "@/api/apiRoute";
import { authData, multipartDataWithToken } from "@/api/apiService.js";

export const useCustomerStore = defineStore("customerStore", {
  state: () => ({
    customerResponse: null,
    allCustomerResponse: null,
    assets: null,
    assetsType: null,
    allBeneficiaries: null,
    _inFlightCustomerRequests: {},
    errorMessage: "",
    showSpouseModal: false,
    showPetModal: false,
    showChildModal: false,
    showNonProfitModal: false,
    showOtherModal: false,
    showSuccessDialog: false,
    showVehicleModal: false,
    showKnicknakkModal: false,
    showCryptocurrencyModal: false,
    showBankModal: false,
    showPropertyModal: false,
    showDigitalAssetModal: false,
    showHeirloomModal: false,
    showOtherPersonModal: false,
    isLoading: false,
    thumbnailImageUrl: null,
    currentTemplateId: null,
    thumbnailUrls: {},
  }),
  getters: {
    /**
     * Get thumbnail URL from cache (reactive)
     */
    getThumbnailUrlById: (state) => (id) => {
      return state.thumbnailUrls[`thumbnail_${id}`] || null;
    },
    
    /**
     * Get thumbnail timestamp for cache busting
     */
    getThumbnailTimestamp: (state) => (id) => {
      return state.thumbnailUrls[`timestamp_${id}`] || Date.now();
    },
  },
  actions: {
    setErrorMessage(message) {
      this.errorMessage = message;
    },
    clearErrorMessage() {
      this.errorMessage = "";
    },
    async submitCustomerObject(payload) {
      // Clear any stale error from a previous request before attempting
      this.clearErrorMessage();
      try {
        const response = await authData.post(`${CUSTOMER_OBJECT}`, payload);
        this.customerResponse = response?.data;
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.errorMessage ||
          "An error occurred";
        this.setErrorMessage(message);
        // Re-throw so callers (useCustomField.submit) can catch and surface the error
        throw error;
      }
    },
    async editCustomerObject(id, payload) {
      try {
        const response = await authData.patch(
          `${CUSTOMER_OBJECT}/${id}`,
          payload
        );
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    async viewCustomerObject(id) {
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}/${id}`);
        this.customerResponse = response?.data;
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    async deleteCustomerObject(id) {
      try {
        const response = await authData.delete(`${CUSTOMER_OBJECT}/${id}`);
        this.customerResponse = response?.data;
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    async getAllCustomerObject() {
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}`);
        this.allCustomerResponse = response?.data?.data;
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    async getCustomerObject(params = {}) {
      this.isLoading = true;
      const key = JSON.stringify({ includeFields: "yes", ...params });
      if (this._inFlightCustomerRequests[key]) {
        return this._inFlightCustomerRequests[key];
      }
      const requestPromise = (async () => {
        try {
          const response = await authData.get(`${CUSTOMER_OBJECT}`, {
            params: {
              includeFields: "yes",
              ...params,
            },
          });
          this.customerResponse = response?.data?.data;
          const grouped = {};
          response.data.data.forEach((item) => {
            const key = item.dataTemplateName;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
          });
          const assetsGrouped = {};
          response.data.data.forEach((item) => {
            const key = item.dataTemplateSlug;
            if (!assetsGrouped[key]) assetsGrouped[key] = [];
            assetsGrouped[key].push(item);
          });
          this.allBeneficiaries = grouped;
          this.assets = assetsGrouped;
          this.clearErrorMessage();
          return handleApiResponse(response);
        } catch (error) {
          this.setErrorMessage(
            error?.errorMessage || error?.message || "An error occurred"
          );
          return handleError(error);
        } finally {
          delete this._inFlightCustomerRequests[key];
          this.isLoading = false;
        }
      })();
      this._inFlightCustomerRequests[key] = requestPromise;
      return requestPromise;
    },
    async updateCustomerObjectField(objectId, fieldId, payload) {
      try {
        const response = await authData.patch(
          UPDATE_CUSTOMER_OBJECT_FIELD(objectId, fieldId),
          payload
        );
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    async updateThumbnail(objectID, thumbnailFile) {
      const formData = new FormData();
      formData.append("file", thumbnailFile);
      try {
        const response = await multipartDataWithToken.post(
          THUMBNAIL_UPLOAD(objectID),
          formData
        );
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },

    async getThumbnail(id) {
      try {
        const response = await authData.get(GET_THUMBNAIL(id), {
          responseType: "arraybuffer",
        });
        this.clearErrorMessage();
        return handleApiResponse(response);
      } catch (error) {
        this.setErrorMessage(
          error?.errorMessage || error?.message || "An error occurred"
        );
        return handleError(error);
      }
    },
    
    // Helper method to detect MIME type from byte array
      detectMimeType(bytes) {
        const signature = (bytes[0] << 8) | bytes[1];
        
        switch (signature) {
          case 0x8950: // PNG
            return "image/png";
          case 0x4749: // GIF
            return "image/gif";
          case 0x424d: // BMP
            return "image/bmp";
          case 0x4949: // TIFF
            return "image/tiff";
          default:
            return "image/jpeg";
        }
      },

    // Helper method to process ArrayBuffer/TypedArray response
    processArrayBufferResponse(data) {
      const bytes = new Uint8Array(data);
      const mimeType = this.detectMimeType(bytes);
      const blob = new Blob([data], { type: mimeType });
      return URL.createObjectURL(blob);
    },

    // Helper method to process different response data types
    processThumbnailData(data) {
      const dataType = this.getDataType(data);
      
      switch (dataType) {
        case "dataUrl":
          return data;
          
        case "blob":
          return URL.createObjectURL(data);
          
        case "arrayBuffer":
          return this.processArrayBufferResponse(data);
          
        case "base64String": {
          // Wrap in braces to create a block scope
          const cleanBase64 = data.trim().replace(/\s/g, "");
          return `data:image/jpeg;base64,${cleanBase64}`;
        }
          
        default:
          return null;
      }
    },

    getDataType(data) {
      if (typeof data === "string" && data.startsWith("data:")) {
        return "dataUrl";
      }
      
      if (data instanceof Blob) {
        return "blob";
      }
      
      if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
        return "arrayBuffer";
      }
      
      if (typeof data === "string") {
        return "base64String";
      }
      
      return "unknown";
    },

    async getThumbnailUrl(id, forceRefresh = false) {
      if (!id) return null;

      const cacheKey = `thumbnail_${id}`;
      
      // Return cached URL if available and not forcing refresh
      if (!forceRefresh && this.thumbnailUrls[cacheKey]) {
        return this.thumbnailUrls[cacheKey];
      }

      try {
        const response = await this.getThumbnail(id);
        
        if (!response?.data) {
          return null;
        }

        const thumbnailUrl = this.processThumbnailData(response.data);
        
        if (thumbnailUrl) {
          // Store in reactive state for caching
          this.thumbnailUrls[cacheKey] = thumbnailUrl;
        }
        
        return thumbnailUrl;
      } catch (error) {
        console.error(`Error fetching thumbnail for ${id}:`, error);
        return null;
      }
    },

    /**
     * Updates thumbnail timestamp for cache busting
     * This makes the browser reload the image by changing the URL parameter
     */
    updateThumbnailTimestamp(id) {
      const timestampKey = `timestamp_${id}`;
      this.thumbnailUrls[timestampKey] = Date.now();
    },

    /**
     * Updates thumbnail and refreshes with new timestamp
     */
    async updateThumbnailWithRefresh(id, file) {
      try {
        await this.updateThumbnail(id, file);
        
        // Update timestamp to force image reload
        this.updateThumbnailTimestamp(id);
        return true;
      } catch (error) {
        console.error("Error updating thumbnail:", error);
        throw error;
      }
    },

    /**
     * Clears thumbnail cache for a specific ID or all
     */
    clearThumbnailCache(id = null) {
      if (id) {
        const cacheKey = `thumbnail_${id}`;
        const timestampKey = `timestamp_${id}`;
        const url = this.thumbnailUrls[cacheKey];
        
        // Revoke blob URLs to prevent memory leaks
        if (url?.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
        
        delete this.thumbnailUrls[cacheKey];
        delete this.thumbnailUrls[timestampKey];
      } else {
        // Clear all
        Object.entries(this.thumbnailUrls).forEach(([key, url]) => {
          if (key.startsWith("thumbnail_") && url?.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
        this.thumbnailUrls = {};
      }
    }
  },
});
